import { useCallback, useEffect, useState } from 'react';
import {
  api,
  type PlatformTicketDetail,
  type PlatformTicketSummary,
  type TicketStatus,
} from '../api.ts';
import { AccountBadge } from '../ClientRow.tsx';
import { plural } from '../format.ts';
import { ReplyForm, StatusChip, Thread } from '../support/Thread.tsx';
import {
  PLATFORM_FILTERS,
  countByStatus,
  formatWhen,
  matchesFilter,
  typeLabel,
} from '../support/tickets.ts';
import { message } from './Platform.tsx';

/**
 * Every account's support tickets, for DriftWatch staff to answer.
 *
 * The whole list is fetched once and filtered here rather than asked for per
 * filter, so the count on each filter tab and the rows under it come from the
 * same answer and cannot disagree.
 *
 * It opens on "Awaiting support" because that is the queue: everything else is
 * either somebody else's move or finished.
 */

type Filter = TicketStatus | 'all';

export const PlatformSupport = () => {
  const [tickets, setTickets] = useState<PlatformTicketSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('awaiting_support');
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(() => {
    void api
      .platformSupportTickets()
      .then((response) => {
        setTickets(response.tickets);
        setError(null);
      })
      .catch((cause: unknown) => setError(message(cause)));
  }, []);

  useEffect(load, [load]);

  if (openId !== null) {
    return (
      <PlatformTicket
        id={openId}
        onBack={() => {
          load();
          setOpenId(null);
        }}
      />
    );
  }

  if (error !== null) {
    return (
      <div className="callout callout-caution" role="alert">
        {error}
      </div>
    );
  }
  if (tickets === null) {
    return (
      <p className="note" role="status">
        Loading tickets…
      </p>
    );
  }

  const queue = countByStatus(tickets, 'awaiting_support');
  const shown = tickets.filter((ticket) => matchesFilter(ticket.status, filter));

  return (
    <section className="pf-stack sp-screen">
      <p className={queue > 0 ? 'lead sp-waiting' : 'lead'} role="status">
        {queue > 0
          ? `${plural(queue, 'ticket')} awaiting support.`
          : 'No ticket is waiting for DriftWatch support.'}{' '}
        Customers are not emailed; a reply here is how they hear back.
      </p>

      <div className="tabs-underline" role="group" aria-label="Filter tickets by status">
        {PLATFORM_FILTERS.map((entry) => {
          const count =
            entry.value === 'all' ? tickets.length : countByStatus(tickets, entry.value);
          return (
            <button
              key={entry.value}
              type="button"
              className={filter === entry.value ? 'tab is-active' : 'tab'}
              aria-pressed={filter === entry.value}
              onClick={() => setFilter(entry.value)}
            >
              {entry.label} <span className="num sp-tab-count">{count}</span>
            </button>
          );
        })}
      </div>

      {shown.length === 0 ? (
        <div className="empty">
          <span className="empty-title">No tickets here</span>
          <p className="empty-body">
            {filter === 'awaiting_support'
              ? 'Every ticket has been answered or closed.'
              : 'No ticket is in this state.'}
          </p>
        </div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl sp-table">
            <thead>
              <tr>
                <th scope="col">Subject</th>
                <th scope="col">Account</th>
                <th scope="col">Type</th>
                <th scope="col">Status</th>
                <th scope="col">Opened by</th>
                <th scope="col">Last updated</th>
                <th scope="col">Messages</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((ticket) => (
                <tr key={ticket.id} className={ticket.status === 'closed' ? 'dimmed' : undefined}>
                  <th scope="row" className="sp-subject">
                    <button type="button" className="link" onClick={() => setOpenId(ticket.id)}>
                      {ticket.subject}
                    </button>
                  </th>
                  <td>
                    <AccountBadge name={ticket.accountName} />
                  </td>
                  <td>{typeLabel(ticket.type)}</td>
                  <td>
                    <StatusChip status={ticket.status} audience="platform" />
                  </td>
                  <td>{ticket.openedByName}</td>
                  <td className="num">{formatWhen(ticket.updatedAt)}</td>
                  <td className="num">{ticket.messageCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

interface PlatformTicketProps {
  readonly id: string;
  readonly onBack: () => void;
}

const PlatformTicket = ({ id, onBack }: PlatformTicketProps) => {
  const [ticket, setTicket] = useState<PlatformTicketDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .platformSupportTicket(id)
      .then((response) => setTicket(response.ticket))
      .catch((cause: unknown) => setError(message(cause)));
  }, [id]);

  const setStatus = async (status: 'closed' | 'awaiting_support') => {
    setChanging(true);
    setStatusError(null);
    try {
      const response = await api.setSupportTicketStatus(id, status);
      setTicket(response.ticket);
    } catch (cause) {
      setStatusError(message(cause));
    } finally {
      setChanging(false);
    }
  };

  return (
    <section className="pf-stack sp-screen">
      <div className="toolbar">
        <button type="button" className="btn btn-quiet btn-sm" onClick={onBack}>
          Back to all tickets
        </button>
      </div>

      {error !== null && (
        <div className="callout callout-caution" role="alert">
          {error}
        </div>
      )}
      {error === null && ticket === null && (
        <p className="note" role="status">
          Loading the ticket…
        </p>
      )}

      {ticket !== null && (
        <article className="card">
          <div className="card-head sp-ticket-head">
            <h2>{ticket.subject}</h2>
            <AccountBadge name={ticket.accountName} />
            <p className="note">
              {typeLabel(ticket.type)} · opened by {ticket.openedByName} on{' '}
              {formatWhen(ticket.createdAt)}
            </p>
            <StatusChip status={ticket.status} audience="platform" />
          </div>
          <div className="card-body sp-stack">
            <Thread messages={ticket.messages} />

            {ticket.status === 'closed' ? (
              <p className="note">
                Closed{ticket.closedAt === null ? '' : ` on ${formatWhen(ticket.closedAt)}`}. The
                customer cannot reply to a closed ticket; reopen it to continue the conversation.
              </p>
            ) : (
              <ReplyForm
                idPrefix="pf-sp-reply"
                label="Reply as DriftWatch support"
                onSend={async (reply) => {
                  const response = await api.platformReplyToSupportTicket(ticket.id, reply);
                  setTicket(response.ticket);
                }}
              />
            )}

            <div className="toolbar sp-status-actions">
              {ticket.status === 'closed' ? (
                <button
                  type="button"
                  className="btn"
                  disabled={changing}
                  aria-busy={changing || undefined}
                  onClick={() => void setStatus('awaiting_support')}
                >
                  {changing ? 'Reopening…' : 'Reopen ticket'}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn"
                  disabled={changing}
                  aria-busy={changing || undefined}
                  onClick={() => void setStatus('closed')}
                >
                  {changing ? 'Closing…' : 'Close ticket'}
                </button>
              )}
              {statusError !== null && (
                <p className="sp-field-error" role="alert">
                  {statusError}
                </p>
              )}
            </div>
          </div>
        </article>
      )}
    </section>
  );
};
